import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "onClick", "onFocus", "onBlur", "variant", "size", "autoFocus"];
import styled from 'foundations-theming/utils';
import { mergeProps } from 'react-aria';
import { useTrellisFocusRing } from '../../hooks/useTrellisFocusRing';
import { useTrellisButton } from '../../hooks/useTrellisButton';
import { Flex } from '../Flex';
import { getVariantTokens } from './CloseButton.helpers';
import { getSizeTokens } from './CloseButton.helpers';
import { useSizeCssObject, useVariantCssObject } from './CloseButton.hooks';
import { RemoveIcon } from 'foundations-assets';
import { filterProps } from '../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledCloseButton = styled.button.withConfig({
  displayName: "CloseButton__StyledCloseButton",
  componentId: "sc-1y82d3h-0"
})(({
  $variantCssStyles,
  $focusRingStyles,
  $sizeCssStyles,
  $spaceCssStyles
}) => {
  return Object.assign({
    //  Reseting all non-inherited properties to initial
    all: 'unset',
    cursor: 'pointer'
  }, $variantCssStyles, $focusRingStyles, $sizeCssStyles, $spaceCssStyles);
});
export function CloseButton(_ref) {
  let {
      disabled = false,
      onClick = () => {},
      onFocus = () => {},
      onBlur = () => {},
      variant = 'default',
      size = 'medium',
      autoFocus = false
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handlePress = () => {
    if (disabled) return;
    onClick();
  };
  const handleFocus = () => {
    onFocus();
  };
  const handleBlur = () => {
    onBlur();
  };
  const variantTokens = getVariantTokens(variant);
  const variantCssStyles = useVariantCssObject(variantTokens);
  const {
    focusRingStyles,
    focusProps
  } = useTrellisFocusRing({
    autoFocus
  });
  const sizeTokens = getSizeTokens(size);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const {
    buttonProps,
    ref: buttonRef
  } = useTrellisButton(Object.assign({}, props, {
    autoFocus,
    onClick: handlePress,
    onFocus: handleFocus,
    onBlur: handleBlur
  }));
  const mergedProps = mergeProps(buttonProps, focusProps);
  const {
    domProps,
    tokenProps
  } = filterProps(props);
  const spaceCssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(StyledCloseButton, Object.assign({}, mergedProps, {
    ref: buttonRef,
    "aria-label": unescapedText('salesUI.UICloseButton.label'),
    "aria-disabled": disabled ? true : undefined,
    $variantCssStyles: variantCssStyles,
    $focusRingStyles: focusRingStyles,
    $sizeCssStyles: sizeCssStyles,
    $spaceCssStyles: spaceCssStyles,
    "data-action": "close" // TODO remove once https://git.hubteam.com/HubSpot/foundations-components/issues/3314 is resolved
  }, domProps, {
    children: /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      as: "span",
      blockSize: "100%",
      children: /*#__PURE__*/_jsx(RemoveIcon, {
        size: sizeTokens.iconSize
      })
    })
  }));
}